<template>
  <div class="comprobante-entrega border rounded p-3">
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
      <div>
        <div class="fw-semibold">{{ titulo }}</div>
        <div v-if="documento.aviso" class="small text-danger mt-1">{{ documento.aviso }}</div>
      </div>
      <span v-if="documento.estado_documento" class="badge bg-light text-dark border">{{ documento.estado_documento }}</span>
    </div>

    <p v-if="!documento.tracking_url" class="small text-muted mt-2 mb-0">
      Esta instancia no tiene dirección pública configurada: no hay enlace para enviar al cliente.
      Se puede imprimir.
    </p>

    <div class="d-flex flex-wrap gap-2 mt-3">
      <button v-if="archivos" type="button" class="btn btn-outline-primary btn-sm" @click="abrir(documento.pdf_url)">
        <i class="bi bi-file-earmark-pdf me-1"></i>Ver PDF
      </button>
      <button v-if="archivos" type="button" class="btn btn-outline-secondary btn-sm" @click="abrir(documento.xml_url)">
        <i class="bi bi-filetype-xml me-1"></i>Descargar XML
      </button>
      <button v-if="documento.tracking_url" type="button" class="btn btn-outline-secondary btn-sm" @click="copiarEnlace">
        <i class="bi bi-link-45deg me-1"></i>{{ copiado ? 'Enlace copiado' : 'Copiar enlace' }}
      </button>
      <button v-if="documento.tracking_url" type="button" class="btn btn-outline-success btn-sm" @click="whatsappAbierto = true">
        <i class="bi bi-whatsapp me-1"></i>WhatsApp
      </button>
      <button v-if="puedeImprimir" type="button" class="btn btn-outline-dark btn-sm" :disabled="imprimiendo" @click="imprimir">
        <span v-if="imprimiendo" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        <i v-else class="bi bi-printer me-1"></i>Imprimir
      </button>
    </div>

    <EnviarSeguimientoModal
      :open="whatsappAbierto"
      :tracking-url="documento.tracking_url || ''"
      :telefono-inicial="documento.telefono_cliente || ''"
      :mensaje="documento.mensaje_whatsapp || ''"
      :titulo="tituloWhatsapp"
      @cerrado="whatsappAbierto = false"
    />
  </div>
</template>

<script setup>
// Panel único de entrega de un comprobante: factura, nota de crédito o factura
// anulada (SPEC_ANULACION_FACTURAS_Y_NOTAS_CREDITO_v0.2.md RN-AN27). Un solo
// juego de botones: una corrección futura se aplica una vez.
import { ref, computed } from 'vue'
import EnviarSeguimientoModal from '@/components/EnviarSeguimientoModal.vue'
import invoiceReprintService from '@/services/invoiceReprintService'
import invoiceLocalPrintService from '@/services/invoiceLocalPrintService'
import { ticketNotaCredito, mensajeDeError } from '@/services/anulacionService'

/* global defineProps */
const props = defineProps({
  // Lo que devuelven /api/sales/:id/entrega y /api/notas-credito/:id/comprobante.
  documento: { type: Object, required: true }
})

const whatsappAbierto = ref(false)
const copiado = ref(false)
const imprimiendo = ref(false)

const tipo = computed(() => String(props.documento?.tipo || 'FACTURA').toUpperCase())
const titulo = computed(() => {
  const n = props.documento?.numero ? ` ${props.documento.numero}` : ''
  if (tipo.value === 'NOTA_CREDITO') return `Nota de crédito${n}`
  if (tipo.value === 'FACTURA_ANULADA') return `Factura${n} (anulada)`
  return `Factura${n}`
})
const tituloWhatsapp = computed(() => (tipo.value === 'NOTA_CREDITO'
  ? 'Enviar nota de crédito por WhatsApp'
  : 'Enviar factura por WhatsApp'))
const archivos = computed(() => Boolean(props.documento?.pdf_url) && props.documento?.archivos !== false)
// La factura anulada no se reimprime en la térmica: un ticket nuevo de un
// documento sin efecto confunde en el mostrador. Sus archivos siguen por PDF/XML.
const puedeImprimir = computed(() => invoiceReprintService.impresionLocalHabilitada()
  && (tipo.value === 'NOTA_CREDITO' || tipo.value === 'FACTURA'))

function abrir(url) {
  if (url) window.open(url, '_blank', 'noopener')
}

async function copiarEnlace() {
  try {
    await navigator.clipboard.writeText(props.documento.tracking_url)
    copiado.value = true
    setTimeout(() => { copiado.value = false }, 2000)
  } catch (error) {
    window.prompt('Copiá el enlace:', props.documento.tracking_url)
  }
}

async function imprimir() {
  imprimiendo.value = true
  try {
    if (tipo.value === 'NOTA_CREDITO') {
      const job = await ticketNotaCredito(props.documento.id_devolucion)
      if (!job) throw new Error('La nota de crédito todavía no tiene ticket disponible.')
      const r = await invoiceLocalPrintService.printFiscalDocument({
        fiscalJob: job,
        idempotencySuffix: `nota-credito-${props.documento.id_devolucion}`
      })
      if (r?.ok === false && r?.message) throw new Error(r.message)
      window.alert.success?.('Nota de crédito enviada a la impresora local.', { title: 'Impresión' })
    } else {
      const r = await invoiceReprintService.reimprimirComprobante({
        saleId: props.documento.id_venta,
        invoiceNumber: props.documento.numero || ''
      })
      if (!r?.omitido) {
        const m = invoiceReprintService.mensajeResultadoReimpresion(r, props.documento.numero || '')
        window.alert[m.tipo]?.(m.texto, { title: 'Impresión' })
      }
    }
  } catch (error) {
    window.alert.error?.(mensajeDeError(error, 'No se pudo imprimir.'), { title: 'Impresión' })
      || window.alert(mensajeDeError(error, 'No se pudo imprimir.'))
  } finally {
    imprimiendo.value = false
  }
}
</script>
